import RenderEntity, { RENDER_ENTITY_FORMAT3, RENDER_ENTITY_FORMAT4 } from 'ui-component/renders/RenderEntity';
import { lazy } from 'react';

// material-ui
import { Stack, Typography, Tooltip, Box } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';
import { FormattedMessage } from 'react-intl';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import RenderWorkflowState, {
    RENDER_WORKFLOW_STATE_FORMAT2,
    RENDER_WORKFLOW_STATE_FORMAT4,
    RENDER_WORKFLOW_STATE_FORMAT5
} from 'ui-component/renders/Workflow/RenderWorkflowState';
import RenderExecSettingsGridCell from 'ui-component/renders/RenderExecSettingsGridCell';
import RenderRecurSettingsGridCell from 'ui-component/renders/RenderRecurSettingsGridCell';

// assets
import FolderTwoToneIcon from '@mui/icons-material/FolderTwoTone';
import PriorityHighTwoToneIcon from '@mui/icons-material/PriorityHighTwoTone';
import DensityMediumTwoToneIcon from '@mui/icons-material/DensityMediumTwoTone';
import LowPriorityTwoToneIcon from '@mui/icons-material/LowPriorityTwoTone';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockSharpIcon from '@mui/icons-material/LockSharp';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

// lazy loaded components
const WorkOrderTypeSelect = lazy(() => import('ui-component/fields/EntitySelect/workmgmt/WorkOrderTypeSelect'));
const DepartmentSelect = lazy(() => import('ui-component/fields/EntitySelect/common/DepartmentSelect'));
const ClientSelect = lazy(() => import('ui-component/fields/EntitySelect/crm/ClientSelect'));
const EmployeeSelect = lazy(() => import('ui-component/fields/EntitySelect/common/EmployeeSelect'));
const BusinessOpportunitySelect = lazy(() => import('ui-component/fields/EntitySelect/workmgmt/BusinessOpportunitySelect'));
const WorkOrderSelect = lazy(() => import('ui-component/fields/EntitySelect/workmgmt/WorkOrderSelect'));
const TaskSelect = lazy(() => import('ui-component/fields/EntitySelect/workmgmt/TaskSelect'));
const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));
const WorkOrderStateSelect = lazy(() => import('ui-component/fields/WorkflowSelect/WorkOrderStateSelect'));
const PrioritySelect = lazy(() => import('ui-component/fields/PrioritySelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(capabilities, params?.row, permissionData, onClickActions);

                if (
                    !params?.row?.deletedAt &&
                    !gridActions?.some((action) => action?.key === `actionedit-row${params?.row?.id}`) &&
                    permissionData?.some((perm) => perm?.startsWith('tasks-'))
                ) {
                    gridActions.push({
                        key: `actionedit-row${params?.row?.id}`,
                        icon: <EditTwoToneIcon fontSize="small" />,
                        label: 'ui-comp-button-edit',
                        position: 2,
                        showInMenu: true,
                        onClick: () => {
                            if (
                                'onClickRowActionEdit' in (onClickActions || {}) &&
                                typeof onClickActions.onClickRowActionEdit === 'function'
                            ) {
                                onClickActions.onClickRowActionEdit(params?.row);
                            }
                        }
                    });
                }

                permissionData?.forEach((perm) => {
                    switch (perm) {
                        case 'canEdit':
                            if (params?.row?.deletedAt) {
                                return;
                            }

                            gridActions.push({
                                key: `actioneditinnewtab-row${params?.row?.id}`,
                                icon: <EditTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-editinnewtab',
                                position: 2.1,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionEditInNewTab' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionEditInNewTab === 'function'
                                    ) {
                                        onClickActions.onClickRowActionEditInNewTab(params?.row);
                                    }
                                }
                            });
                            break;
                        default: // do nothing
                    }
                });

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'code',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={params.value}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <FolderTwoToneIcon
                                sx={{
                                    fontSize: '0.875rem',
                                    color:
                                        'r' in (params?.row?.tplWorkOrderColor || {}) &&
                                        'g' in (params?.row?.tplWorkOrderColor || {}) &&
                                        'b' in (params?.row?.tplWorkOrderColor || {}) &&
                                        'a' in (params?.row?.tplWorkOrderColor || {})
                                            ? `rgba(${params.row.tplWorkOrderColor.r}, ${params.row.tplWorkOrderColor.g}, ${params.row.tplWorkOrderColor.b}, ${params.row.tplWorkOrderColor.a})`
                                            : 'secondary'
                                }}
                            />
                            <Typography
                                variant="inherit"
                                color="inherit"
                                sx={{
                                    overflow: 'hidden',
                                    display: 'block',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                    // ':hover': { color: 'primary.main' }
                                    // cursor: 'pointer'
                                }}
                            >
                                {params.value}
                            </Typography>
                        </Stack>
                    </Tooltip>
                ),
            label: 'traits-appentityhasautogencode-code'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Tooltip
                    title={
                        <div
                            style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1…') }}
                        />
                    }
                >
                    <div
                        style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1…') }}
                    />
                </Tooltip>
            ),
            flex: 1,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'workOrderType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="workOrderType"
                    renderPattern="<name>"
                    renderTooltipPattern="[<acronym>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-workordertype'
        },
        {
            field: 'businessOpportunity',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="businessOpportunity"
                    renderPattern="<name>"
                    renderTooltipPattern="[<code>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-workorder-businessopportunity'
        },
        {
            field: 'department',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="department"
                    renderPattern="<name>"
                    renderTooltipPattern="<name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-common-department'
        },
        {
            field: 'client',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="client"
                    renderPattern="<name>"
                    renderTooltipPattern="[<code>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT4}
                    icon={params?.row?.client?.partnershipType?.icon}
                    color={params?.row?.client?.partnershipType?.color}
                    {...params}
                />
            ),
            label: 'entity-crm-client'
        },
        {
            field: 'priority',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={<FormattedMessage id={`ui-comp-form-priority-${params.value || 'low'}`} />}>
                        <Box>
                            {params.value === 'low' && <LowPriorityTwoToneIcon fontSize="small" />}
                            {params.value === 'medium' && <DensityMediumTwoToneIcon fontSize="small" />}
                            {params.value === 'high' && <PriorityHighTwoToneIcon fontSize="small" />}
                        </Box>
                    </Tooltip>
                ),
            label: 'entity-workmgmt-workorder-priority'
        },
        {
            field: 'responsible',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="employee"
                    renderPattern="<name> <lastName>"
                    renderTooltipPattern="[<code>] <name> <lastName>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-workorder-responsible'
        },
        {
            field: 'tasks',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT4} {...params} />,
            label: 'entity-workmgmt-task-plural'
        },
        {
            field: 'taskFollowUps',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT5} {...params} />,
            label: 'entity-workmgmt-workorder-taskfollowups'
        },
        {
            field: 'state',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT2} {...params} />,
            label: 'entity-workmgmt-workorder-state'
        },
        {
            field: 'previousState',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT2} {...params} />,
            label: 'entity-workmgmt-workorder-previousstate'
        },
        {
            field: 'execSettings',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => <RenderExecSettingsGridCell {...params} />,
            label: 'entity-workmgmt-execsettings'
        },
        {
            field: 'recurSettings',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => <RenderRecurSettingsGridCell {...params} />,
            label: 'entity-workmgmt-recursettings'
        },
        {
            field: 'ticketCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-ticket-plural'
        },
        {
            field: 'notesCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-note-plural'
        },
        {
            field: 'sourceWorkOrder',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="workOrder"
                    renderPattern="<name>"
                    renderTooltipPattern="[<code>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-workorder-sourceworkorder'
        },
        {
            field: 'sourceTask',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="task"
                    renderPattern="<name>"
                    renderTooltipPattern="[<code>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-workorder-sourcetask'
        },
        {
            field: 'workOrderCopiesCount',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-workorder-workordercopies'
        },
        {
            field: 'cancellationReason',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Tooltip
                    title={
                        <div
                            style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1…') }}
                        />
                    }
                >
                    <div
                        style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1…') }}
                    />
                </Tooltip>
            ),
            label: 'entity-workmgmt-workorder-cancellationreason'
        },
        {
            field: 'locked',
            type: 'boolean',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Box>
                    {params?.value ? (
                        <LockSharpIcon fontSize="small" color="error" />
                    ) : (
                        <LockOpenOutlinedIcon fontSize="small" color="info" />
                    )}
                </Box>
            ),
            label: 'traits-appentityislockable-locked'
        },
        {
            field: 'lockedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityislockable-lockedat'
        },
        {
            field: 'lockedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityislockable-lockedby'
        },
        {
            field: 'lockType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={<FormattedMessage id={`ui-comp-form-locktype-${params.value}`} />}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FormattedMessage id={`ui-comp-form-locktype-${params.value}`} />
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityislockable-locktype'
        },
        {
            field: 'lockTimeout',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityislockable-locktimeout'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                code: true,
                name: true,
                description: false,
                workOrderType: false,
                businessOpportunity: false,
                department: true,
                client: true,
                priority: true,
                responsible: true,
                tasks: true,
                taskFollowUps: true,
                state: true,
                previousState: false,
                execSettings: true,
                recurSettings: true,
                ticketCount: false,
                notesCount: false,
                sourceWorkOrder: false,
                sourceTask: false,
                workOrderCopiesCount: false,
                cancellationReason: false,
                locked: true,
                lockedAt: false,
                lockedBy: false,
                lockType: false,
                lockTimeout: false,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'code',
                'name',
                'description',
                'workOrderType',
                'businessOpportunity',
                'department',
                'client',
                'priority',
                'responsible',
                'tasks',
                'taskFollowUps',
                'state',
                'previousState',
                'execSettings',
                'recurSettings',
                'ticketCount',
                'notesCount',
                'sourceWorkOrder',
                'sourceTask',
                'workOrderCopiesCount',
                'cancellationReason',
                'locked',
                'lockedAt',
                'lockedBy',
                'lockType',
                'lockTimeout',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                code: { width: 100 },
                name: { width: 200 },
                description: { width: 100 },
                workOrderType: { width: 100 },
                businessOpportunity: { width: 150 },
                department: { width: 100 },
                client: { width: 100 },
                priority: { width: 100 },
                responsible: { width: 100 },
                tasks: { width: 100 },
                taskFollowUps: { width: 100 },
                state: { width: 100 },
                previousState: { width: 100 },
                execSettings: { width: 100 },
                recurSettings: { width: 100 },
                ticketCount: { width: 100 },
                notesCount: { width: 100 },
                sourceWorkOrder: { width: 100 },
                sourceTask: { width: 100 },
                workOrderCopiesCount: { width: 100 },
                cancellationReason: { width: 100 },
                locked: { width: 100 },
                lockedAt: { width: 100 },
                lockedBy: { width: 100 },
                lockType: { width: 100 },
                lockTimeout: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        },
        toolbarFilters: { loadUserTasks: false, includeSafeDeleted: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'code', fieldType: 'simple' },
            label: 'traits-appentityhasautogencode-code'
        },
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'workOrderTypeId', fieldType: 'simple' },
            label: 'entity-workmgmt-workordertype',
            renderField: (params) => <WorkOrderTypeSelect key="filterWorkOrder-workOrderType" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'businessOpportunityId', fieldType: 'simple' },
            label: 'entity-workmgmt-workorder-businessopportunity',
            renderField: (params) => <BusinessOpportunitySelect key="filterWorkOrder-businessOpportunity" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'departmentId', fieldType: 'simple' },
            label: 'entity-common-department',
            renderField: (params) => <DepartmentSelect key="filterWorkOrder-department" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'clientId', fieldType: 'simple' },
            label: 'entity-crm-client',
            renderField: (params) => <ClientSelect key="filterWorkOrder-client" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'priority', fieldType: 'simple' },
            label: 'entity-workmgmt-workorder-priority',
            renderField: (params) => <PrioritySelect key="filterWorkOrder-priority" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'responsibleId', fieldType: 'simple' },
            label: 'entity-workmgmt-workorder-responsible',
            renderField: (params) => <EmployeeSelect key="filterWorkOrder-responsible" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'state', fieldType: 'simple' },
            label: 'entity-workmgmt-workorder-state',
            renderField: (params) => <WorkOrderStateSelect key="filterWorkOrder-state" multiple {...params} />
        },
        {
            filterType: 'array',
            field: { fieldName: 'previousState', fieldType: 'simple' },
            label: 'entity-workmgmt-workorder-previousstate',
            renderField: (params) => <WorkOrderStateSelect key="filterWorkOrder-previousState" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'sourceWorkOrder', fieldType: 'simple' },
            label: 'entity-workmgmt-workorder-sourceworkorder',
            renderField: (params) => <WorkOrderSelect key="filterWorkOrder-sourceWorkOrder" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'sourceTaskId', fieldType: 'simple' },
            label: 'entity-workmgmt-workorder-sourcetask',
            renderField: (params) => <TaskSelect key="filterWorkOrder-sourceTask" multiple {...params} />
        },
        {
            filterType: 'string',
            field: { fieldName: 'cancellationReason', fieldType: 'simple' },
            label: 'entity-workmgmt-workorder-cancellationreason'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'lockedBy', fieldType: 'simple', outputFieldName: 'lockedById' },
            label: 'traits-appentityislockable-lockedby',
            renderField: (params) => <UserSelect key="filterWorkOrder-lockedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => (
                <UserSelect key="filterBusinessOpportunityType-createdBy" multiple displayMode="fullName" {...params} />
            )
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => (
                <UserSelect key="filterBusinessOpportunityType-updatedBy" multiple displayMode="fullName" {...params} />
            )
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => (
                <UserSelect key="filterBusinessOpportunityType-deletedBy" multiple displayMode="fullName" {...params} />
            )
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-workorder',
        fields: [
            { fieldName: 'code', fieldType: 'simple' },
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'workOrderTypeAcronym', fieldType: 'simple' },
            { fieldName: 'workOrderTypeName', fieldType: 'simple' },
            { fieldName: 'clientCode', fieldType: 'simple' },
            { fieldName: 'clientName', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'code', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'workOrderTypeId', fieldType: 'simple' },
        { fieldName: 'departmentId', fieldType: 'simple' },
        { fieldName: 'clientId', fieldType: 'simple' },
        { fieldName: 'priority', fieldType: 'simple' },
        { fieldName: 'responsibleId', fieldType: 'simple' },
        { fieldName: 'state', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' },
        { fieldName: 'deletedAt', fieldType: 'simple' },
        { fieldName: 'deletedBy', fieldType: 'simple' }
    ]
};

export default initialState;
